import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotify, DeleteWithConfirmButton } from 'react-admin';
import InputMask from 'react-input-mask';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Hidden from '@material-ui/core/Hidden';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import EmailIcon from '@material-ui/icons/Email';
import PhoneSharpIcon from '@material-ui/icons/PhoneSharp';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useCustomerDetailsStyles } from './CustomerDetails';
import {
  convertPhoneFormat,
  emailValidationRegExp,
  getZ3PConfigValue,
} from '../../helper';
import { apiGet, apiPost, apiPut } from '../../brainApi';
import UsersView from './UsersView';

const CustomerDetailsHeader = ({ customer, setCustomer }) => {
  const classes = useCustomerDetailsStyles();
  const notify = useNotify();
  const minWidth800 = useMediaQuery('(min-width:800px)');

  const [openContactEdit, setOpenContactEdit] = useState(false);
  const [editedContactInfo, setEditedContactInfo] = useState({});
  const [userToEdit, setUserToEdit] = useState({});
  const [primary, setPrimary] = useState({});
  const [subusers, setSubusers] = useState([]);
  const [contactIsNew, setContactIsNew] = useState(false);
  const [isPrimary, setIsPrimary] = useState(null);
  const [isNewPrimary, setIsNewPrimary] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [haveFetchedSubusers, setHaveFetchedSubusers] = useState(false);

  const allowsSubusers = getZ3PConfigValue('allows_subusers');

  const setSortedSubusers = useCallback((users) => {
    setSubusers(users.sort((a, b) => a.id - b.id));
  }, []);

  useEffect(() => {
    if (customer && allowsSubusers !== null) {
      const primaryUser = {
        firstName: customer.first_name,
        lastName: customer.last_name,
        email: customer.email,
        phone: convertPhoneFormat(customer.phone),
        id: customer.id,
        isPrimary: true,
      };
      setPrimary(primaryUser);
      if (allowsSubusers && !haveFetchedSubusers) {
        apiGet(`/customer/${customer.id}/subusers`).then((res) => {
          const initialSubusers = res
            .filter((subuser) => !subuser.is_deleted)
            .map((subuser) => ({
              firstName: subuser.first_name,
              lastName: subuser.last_name,
              email: subuser.username,
              id: subuser.id,
              isPrimary: false,
            }));
          setSortedSubusers(initialSubusers);
          setIsLoading(false);
          setHaveFetchedSubusers(true);
        });
      } else {
        setIsLoading(false);
      }
    }
  }, [customer, allowsSubusers, setSortedSubusers, haveFetchedSubusers]);

  const onChangeContactInfo = (key) => (e) => {
    const { value } = e.target;
    setEditedContactInfo((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCancel = () => {
    setOpenContactEdit(false);
    setIsPrimary(false);
    setIsNewPrimary(false);
  };

  const isFormDirty = () =>
    userToEdit.firstName !== editedContactInfo.firstName ||
    userToEdit.lastName !== editedContactInfo.lastName ||
    userToEdit.email !== editedContactInfo.email ||
    userToEdit.phone !== editedContactInfo.phone;

  const handleClickNewUser = () => {
    if (!primary?.email) {
      notify(
        'Primary contact must have an email address on file before additional users can be added',
        'warning',
      );
      return;
    }
    setEditedContactInfo({});
    setContactIsNew(true);
    setOpenContactEdit(true);
  };

  const updateCustomerOrUser = async (payload) => {
    const route = `/${isPrimary || isNewPrimary ? 'customer' : 'subuser'}/${
      isNewPrimary ? primary.id : editedContactInfo.id
    }`;
    try {
      setIsSaving(true);
      // If the user is changing AND becoming the primary, we need to update the user first
      if (isNewPrimary && isFormDirty() && !contactIsNew) {
        await apiPut(`/subuser/${editedContactInfo.id}`, payload);
      }
      const res = await apiPut(route, payload);
      if (res.Status === 'Failure') {
        notify(res.Message, 'warning');
        return Promise.resolve();
      }
      notify(
        `${
          isPrimary || isNewPrimary ? 'Customer' : 'User'
        } updated successfully`,
      );
      const updatedCustomerInfo = {
        firstName: res.first_name,
        lastName: res.last_name,
        email: res.email || res.username,
        phone: convertPhoneFormat(res.phone),
        id: res.id,
      };
      if (isNewPrimary) {
        // Remove newPrimary from users
        const updatedUsers = subusers.filter(
          (u) => u.id !== editedContactInfo.id,
        );
        // Find old primary from list of subusers based on matching email
        // to use its id
        const oldPrimary = res.subusers.find(
          (u) => u.username === primary.email,
        );
        updatedUsers.push({
          firstName: primary.firstName,
          lastName: primary.lastName,
          email: primary.email,
          id: oldPrimary.id,
          isPrimary: false,
        });
        setSortedSubusers(updatedUsers);
      }
      if (isPrimary || isNewPrimary) {
        setPrimary({
          ...updatedCustomerInfo,
          isPrimary: true,
        });
        setCustomer((prev) => ({
          ...prev,
          first_name: res.first_name,
          last_name: res.last_name,
          phone: res.phone,
          email: res.email,
        }));
      } else {
        const thisUser = subusers.find((u) => u.id === editedContactInfo.id);
        const userIndex = subusers.indexOf(thisUser);
        const updatedUsers = [...subusers];
        updatedUsers[userIndex] = {
          ...updatedCustomerInfo,
          id: editedContactInfo.id,
          isPrimary: false,
        };
        setSortedSubusers(updatedUsers);
      }
      setIsPrimary(false);
      setIsNewPrimary(false);
      setContactIsNew(false);
      setOpenContactEdit(false);
      return Promise.resolve();
    } catch (error) {
      notify(error.message, 'warning');
      return Promise.resolve();
    } finally {
      setIsSaving(false);
    }
  };

  const createSubuser = async (payload) => {
    try {
      setIsSaving(true);
      const res = await apiPost(`/subuser`, payload);
      if (res.Status === 'Failure') {
        notify(res.Message, 'warning');
      } else {
        notify('User created successfully');
        const updatedCustomerInfo = {
          firstName: res.first_name,
          lastName: res.last_name,
          email: res.username,
        };
        const updatedUsers = [...subusers];
        updatedUsers.push({
          ...updatedCustomerInfo,
          id: res.id,
          isPrimary: false,
        });
        setSortedSubusers(updatedUsers);
        if (isNewPrimary) {
          updateCustomerOrUser(payload);
        } else {
          setContactIsNew(false);
          setOpenContactEdit(false);
        }
      }
    } catch (error) {
      notify(error.message, 'warning');
    } finally {
      setIsSaving(false);
    }
  };

  const formatPayload = () => {
    const payload = {
      first_name: editedContactInfo.firstName,
      last_name: editedContactInfo.lastName || '',
      phone: editedContactInfo.phone,
    };
    if (isPrimary || isNewPrimary) {
      payload.email = editedContactInfo.email || null;
    } else {
      payload.username = editedContactInfo.email;
    }
    if (isNewPrimary) {
      payload.username = editedContactInfo.email;
      payload.skip_user = true;
    }
    if (contactIsNew) {
      payload.customer_id = primary.id;
    }
    return payload;
  };

  const handleSave = () => {
    const payload = formatPayload();
    if (contactIsNew) {
      createSubuser(payload);
    } else {
      updateCustomerOrUser(payload);
    }
  };

  const handleEdit = (userId, editPrimary = false) => {
    const thisUser = editPrimary
      ? primary
      : subusers.find((u) => u.id === userId);
    setIsPrimary(editPrimary);
    setUserToEdit(thisUser);
    setEditedContactInfo({ ...thisUser });
    setOpenContactEdit(true);
  };

  const handlePrimary = () => {
    setIsNewPrimary(!isNewPrimary);
  };

  const firstNameHelperText = useMemo(() => {
    if (!editedContactInfo.firstName) return 'Required';
    return null;
  }, [editedContactInfo.firstName]);

  const emailHelperText = useMemo(() => {
    const emailAddress = editedContactInfo.email;
    if (!emailAddress && allowsSubusers) {
      return 'Required';
    }
    if (emailAddress && !emailAddress?.match(emailValidationRegExp())) {
      return 'Invalid email address';
    }
    return null;
  }, [allowsSubusers, editedContactInfo.email]);

  const phoneHelperText = useMemo(() => {
    // Filters out non-numeric characters, ex. '+1(2)a 3-4' --> '1234'
    const numericPhone = editedContactInfo.phone?.replace(/[^0-9]/g, '');
    if (!numericPhone?.length) {
      return 'Required';
    }
    if (![10, 11].includes(numericPhone?.length)) {
      return 'Invalid phone length';
    }
    return null;
  }, [editedContactInfo.phone]);

  return (
    <Paper
      className={classes.detailsHeader}
      style={{
        flexWrap: minWidth800 ? 'nowrap' : 'wrap',
        justifyContent: minWidth800 ? 'space-between' : 'center',
      }}
    >
      <Box
        display="flex"
        width={minWidth800 ? 'auto' : '100%'}
        justifyContent={minWidth800 ? 'flex-start' : 'center'}
        marginBottom={minWidth800 ? '0' : '24px'}
      >
        <Hidden smDown>
          <AccountBoxIcon
            className={classes.profilePhoto}
            viewBox="3 3 18 18"
          />
        </Hidden>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent={
            !allowsSubusers || (openContactEdit && isPrimary)
              ? 'space-between'
              : 'flex-start'
          }
        >
          {openContactEdit ? (
            <>
              <Box
                display="flex"
                justifyContent={minWidth800 ? 'flex-start' : 'center'}
              >
                <TextField
                  placeholder="First name"
                  value={editedContactInfo.firstName}
                  autoFocus
                  className={classes.cardTextContent}
                  onChange={onChangeContactInfo('firstName')}
                  helperText={firstNameHelperText}
                  error={!!firstNameHelperText}
                />
                <TextField
                  placeholder="Last name (optional)"
                  value={editedContactInfo.lastName}
                  className={classes.cardTextContent}
                  onChange={onChangeContactInfo('lastName')}
                />
              </Box>

              <Box
                display="flex"
                justifyContent={minWidth800 ? 'flex-start' : 'center'}
                style={{ paddingTop: 5 }}
              >
                <Hidden smDown>
                  <EmailIcon
                    className={classes.contactIcon}
                    style={{ marginBottom: '8px' }}
                  />
                </Hidden>
                <TextField
                  placeholder={`Email${allowsSubusers ? '' : ' (optional)'}`}
                  value={editedContactInfo.email}
                  className={classes.cardTextContent}
                  onChange={onChangeContactInfo('email')}
                  helperText={emailHelperText}
                  error={!!emailHelperText}
                />
              </Box>
              {(isPrimary || isNewPrimary) && (
                <Box
                  display="flex"
                  justifyContent={minWidth800 ? 'flex-start' : 'center'}
                >
                  <Hidden smDown>
                    <PhoneSharpIcon className={classes.contactIcon} />
                  </Hidden>
                  <InputMask
                    mask="(999) 999-9999"
                    placeholder="Phone"
                    value={editedContactInfo.phone}
                    className={classes.cardTextContent}
                    onChange={onChangeContactInfo('phone')}
                    helperText={phoneHelperText}
                    error={!!phoneHelperText}
                  >
                    {(inputProps) => <TextField {...inputProps} />}
                  </InputMask>
                </Box>
              )}
              {allowsSubusers && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPrimary || isNewPrimary}
                      name="Primary Contact"
                      color="primary"
                      disabled={isPrimary}
                    />
                  }
                  label="Primary Contact"
                  onChange={handlePrimary}
                />
              )}
            </>
          ) : (
            <>
              <Typography variant="h3" align={minWidth800 ? 'left' : 'center'}>
                {`${primary.firstName} ${primary.lastName}`}
              </Typography>
              <UsersView
                isLoading={isLoading}
                allowsSubusers={allowsSubusers}
                primary={primary}
                subusers={subusers}
                handleEdit={handleEdit}
                handleClickNewUser={handleClickNewUser}
                setSortedSubusers={setSortedSubusers}
              />
            </>
          )}
        </Box>
      </Box>
      <Box>
        {openContactEdit ? (
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
          >
            <Box display="flex">
              <Button
                size="medium"
                color="primary"
                className={classes.nowrap}
                onClick={handleCancel}
                style={{ marginRight: '24px' }}
              >
                Cancel
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                className={classes.nowrap}
                onClick={handleSave}
                disabled={
                  isSaving ||
                  !!firstNameHelperText ||
                  !!emailHelperText ||
                  ((isPrimary || isNewPrimary) && !!phoneHelperText) ||
                  !isFormDirty()
                }
              >
                {isSaving && (
                  <CircularProgress size={20} style={{ marginRight: 10 }} />
                )}
                <Typography>Save</Typography>
              </Button>
            </Box>
            {isPrimary && (
              <DeleteWithConfirmButton
                resource="customer"
                record={customer}
                label="Delete customer"
                confirmTitle={`Delete ${[primary.firstName, primary.lastName]
                  .filter(Boolean)
                  .join(' ')}`}
                confirmContent={`Are you sure you want to delete this customer?${
                  allowsSubusers
                    ? ' This will delete the primary user and all additional users.'
                    : ''
                }`}
                redirect="/customer"
              />
            )}
          </Box>
        ) : (
          <Box>
            {!allowsSubusers && (
              <Button
                size="medium"
                variant="contained"
                color="primary"
                className={classes.nowrap}
                onClick={() => handleEdit(primary.id, true)}
              >
                Edit Customer
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default CustomerDetailsHeader;
